// A person's name and picture (02-building-blocks.md, "A person's name" and "A person's picture";
// 17-people.md, "Names everywhere"): the room's, else the profile's, else their directory entry's (the
// chat server's user directory, the company's people directory, the name they were picked by), else
// the local part of their ID; never a chat ID. The directory is asked only when the room and the
// profile give no name, at most once per person per 10 minutes, and its answer is kept in memory.
import { EventType, RoomEvent, RoomNameType, type MatrixClient, type Room, type RoomNameState } from 'matrix-js-sdk';
import { useEffect, useState } from 'react';
import { t } from '../i18n';
import { admin, inCompany } from '../session/admin';

/** The part of a chat ID between "@" and ":". */
export const localPart = (userId: string) => userId.slice(1).split(':')[0];
const isId = (text: string) => /^@[^:\s]+:\S+$/.test(text.trim());
/** A name worth showing: not empty and not a chat ID. */
export const shownName = (name: string | null | undefined): string | undefined => (name?.trim() && !isId(name) ? name : undefined);

interface Colleague {
  name: string;
  email: string;
  chatId: string | null;
}
let directory: { at: number; people: Promise<Colleague[]> } | undefined;

/** The company's people directory (17), all pages, kept up to 10 minutes; unreadable means none. */
export function colleagues(client: MatrixClient): Promise<Colleague[]> {
  if (!inCompany(client)) return Promise.resolve([]);
  if (!directory || Date.now() - directory.at > 600_000) {
    directory = {
      at: Date.now(),
      people: (async () => {
        const all: Colleague[] = [];
        let after: string | null = null;
        for (let page = 0; page < 50; page++) {
          const answer: { people: Colleague[]; next: string | null } = await admin(client, `/directory?limit=200${after ? `&after=${encodeURIComponent(after)}` : ''}`);
          all.push(...answer.people);
          if (!(after = answer.next)) break;
        }
        return all;
      })().catch(() => []),
    };
  }
  return directory.people;
}

interface Found {
  name?: string;
  mxc?: string;
}
const TEN_MINUTES = 600_000;
const asked = new Map<string, { at: number; found?: Found }>();
const picked = new Map<string, Found>();
const listeners = new Set<() => void>();

/** The profile, else the chat server's user directory (exactly that ID), else the people directory. */
async function ask(client: MatrixClient, userId: string): Promise<Found> {
  const profile = await client.getProfileInfo(userId).catch(() => undefined);
  if (shownName(profile?.displayname)) return { name: profile!.displayname, mxc: profile!.avatar_url };
  const entry = await client.searchUserDirectory({ term: localPart(userId), limit: 50 }).then(
    (a) => a.results.find((r) => r.user_id === userId),
    () => undefined,
  );
  const name = shownName(entry?.display_name) ?? shownName((await colleagues(client)).find((c) => c.chatId === userId)?.name);
  return { name, mxc: profile?.avatar_url || entry?.avatar_url || undefined };
}

/** What the directory said (asking it when it is time), else the name and picture of the pick. */
function fromDirectory(client: MatrixClient, userId: string): Found {
  const entry = asked.get(userId);
  if (!entry || Date.now() - entry.at >= TEN_MINUTES) {
    const at = Date.now();
    asked.set(userId, { at, found: entry?.found });
    void ask(client, userId).then((found) => {
      const before = asked.get(userId)?.found;
      asked.set(userId, { at, found });
      if (found.name !== before?.name || found.mxc !== before?.mxc) changed(client, userId);
    });
  }
  const found = asked.get(userId)?.found;
  const pick = picked.get(userId);
  return { name: found?.name ?? pick?.name, mxc: found?.mxc ?? pick?.mxc };
}

/** The rooms showing the person take their name again; every place drawing names draws again. */
function changed(client: MatrixClient, userId: string) {
  for (const room of client.getRooms?.() ?? []) {
    if (!room.getMember(userId) && room.getAvatarFallbackMember()?.userId !== userId) continue;
    const before = room.name;
    room.recalculate();
    if (room.name === before) room.emit(RoomEvent.Name, room);
  }
  listeners.forEach((l) => l());
}

function who(client: MatrixClient | undefined, userId: string, room?: Room | null): { name: string; mxc?: string } {
  const member = room?.getMember(userId);
  const user = client?.getUser(userId);
  const mxc = member?.getMxcAvatarUrl() || user?.avatarUrl || undefined;
  const name = shownName(member?.rawDisplayName) ?? shownName(user?.displayName);
  if (name || !client) return { name: name ?? localPart(userId), mxc: mxc ?? asked.get(userId)?.found?.mxc ?? picked.get(userId)?.mxc };
  const found = fromDirectory(client, userId);
  return { name: found.name ?? localPart(userId), mxc: mxc ?? found.mxc };
}

/** A person's name (02): in the room, else their profile's, else their directory name, else their local part. */
export const personName = (client: MatrixClient | undefined, userId: string, room?: Room | null) => who(client, userId, room).name;

/** A person's picture (02): in the room, else their profile's, else their user directory entry's; none: initials. */
export const personPicture = (client: MatrixClient | undefined, userId: string, room?: Room | null) => who(client, userId, room).mxc;

/** The name and picture a person was picked by (17): shown from the start of a new chat with them. */
export function rememberPick(client: MatrixClient, userId: string, name: string, mxc?: string) {
  picked.set(userId, { name: name !== localPart(userId) ? shownName(name) : undefined, mxc });
  changed(client, userId);
}

/** Draws again when a directory answer or a pick changes a name or picture. */
export function useNames() {
  const [, set] = useState(0);
  useEffect(() => {
    const l = () => set((n) => n + 1);
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
}

/** A group's name from its people (02): "A", "A and B", "A, B and C", "A, B, C and N more" (`total`: all of them). */
function namesOf(names: string[], total: number): string {
  const [a, b, c] = names;
  if (total > 3 && c) return t('frame.namesMore', { a, b, c, n: total - 3 });
  return c ? t('frame.namesThree', { a, b, c }) : b ? t('frame.namesTwo', { a, b }) : a;
}

/** The person a direct chat is listed for (m.direct). */
const directPerson = (client: MatrixClient, roomId: string) =>
  Object.entries(client.getAccountData(EventType.Direct)?.getContent<Record<string, string[]>>() ?? {}).find(([, rooms]) => rooms?.includes(roomId))?.[0];

/** The other member who left or was removed last. */
const lastOther = (room: Room, me?: string | null) =>
  room
    .getMembers()
    .filter((m) => m.userId !== me && (m.membership === 'leave' || m.membership === 'ban'))
    .sort((x, y) => (y.events.member?.getTs() ?? 0) - (x.events.member?.getTs() ?? 0))[0]?.userId;

/**
 * The person a direct chat shows (its name and picture, 02): the other member, and once they left or
 * nobody else is in it any more, the person m.direct names for it, else the last other member.
 */
export function chatPerson(client: MatrixClient, room: Room): { userId: string } | undefined {
  const me = client.getUserId();
  const member = room.getAvatarFallbackMember();
  if (member && member.userId !== me && (member.membership === 'join' || member.membership === 'invite')) return member;
  const userId = directPerson(client, room.roomId) ?? lastOther(room, me) ?? (member?.userId !== me ? member?.userId : undefined);
  return userId ? { userId } : undefined;
}

/** A chat without its own name or address (a group named from its people), not a space. */
export const unnamedGroup = (room: Room) =>
  !room.isSpaceRoom() && !room.currentState.getStateEvents(EventType.RoomName, '')?.getContent().name && !room.getCanonicalAlias();

/**
 * The client's room names (02, "A channel's name"): a room's own name or address as the SDK has it.
 * A chat without one is named from up to three of the SDK's heroes (in their order) by the person
 * rule, then "and N more"; an empty or left direct chat by its person (`chatPerson`). Never an ID,
 * never the SDK's English words.
 */
export const roomNames =
  (client: () => MatrixClient | undefined) =>
  (roomId: string, state: RoomNameState): string | null => {
    // Its own name or address; a list of people invited by email stays the SDK's.
    if (state.type === RoomNameType.Actual || (state.type === RoomNameType.Generated && state.subtype)) return null;
    const c = client();
    const room = c?.getRoom(roomId) ?? undefined;
    if (!c) return state.type === RoomNameType.Generated && state.names.length ? namesOf(state.names.map((n) => (isId(n) ? localPart(n.trim()) : n)), state.count) : null;
    // Members not loaded yet (after a fresh sign-in): loaded, so the people's own names can come (02).
    if (room && !room.membersLoaded()) void room.loadMembersIfNeeded().then(() => room.recalculate(), () => {});
    const me = c.getUserId();
    if (state.type === RoomNameType.Generated) {
      // The SDK's heroes (kept by the room from the sync's summary; no public getter), else its own
      // choice: the other members by their IDs' order.
      const heroes = (room as unknown as { heroes?: { userId: string }[] | null } | undefined)?.heroes?.map((h) => h.userId).filter((id) => id !== me);
      const collator = new Intl.Collator();
      const ids = heroes?.length
        ? heroes
        : (room?.getMembers() ?? [])
            .filter((m) => m.userId !== me && (m.membership === 'join' || m.membership === 'invite'))
            .map((m) => m.userId)
            .sort((x, y) => collator.compare(x, y));
      const names = ids.length ? ids.slice(0, 3).map((id) => personName(c, id, room)) : state.names.slice(0, 3).map((n) => (isId(n) ? personName(c, n.trim(), room) : n));
      return names.length ? namesOf(names, Math.max(state.count, ids.length, names.length)) : null;
    }
    // Empty, or the other person left: the direct chat's person.
    const userId = room ? chatPerson(c, room)?.userId : directPerson(c, roomId);
    if (userId) return personName(c, userId, room);
    // An unnamed group everyone else left: the people who were in it, never the SDK's "Empty room" (02, v1.58.22).
    return state.type === RoomNameType.EmptyRoom && state.oldName ? state.oldName : null;
  };

/** For the unit tests: forgets every answer and pick. */
export function forgetNames() {
  asked.clear();
  picked.clear();
  directory = undefined;
}
